import React, { useState, useEffect, useMemo } from 'react';
import { SpeedDialProps, SpeedDialData, BookmarkItem } from './types';
import TabNavigation from './TabNavigation';
import SpeedDialCard from './SpeedDialCard';
import Modal from './Modal';

// Editor extends Props but doesn't necessarily need defaultColumns from props as it can be edited
const SpeedDialEdit: React.FC<SpeedDialProps> = ({
    data: initialData,
    defaultColumns = 5,
    defaultActiveTab,
    onSave,
    onTabChange,
    onEnableEdit,
}) => {
    const [data, setData] = useState<SpeedDialData>(JSON.parse(JSON.stringify(initialData)));
    const tabs = Object.keys(data);
    if (!defaultActiveTab) defaultActiveTab = tabs[defaultActiveTab ?? 0];
    const [activeTab, setActiveTab] = useState<string>(defaultActiveTab || '');

    // -- EDITING STATE --
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<BookmarkItem | null>(null);
    const [editingItemIndex, setEditingItemIndex] = useState<number>(-1); // -1 means new item

    const [isTabModalOpen, setIsTabModalOpen] = useState(false);

    // -- CONFIRMATION MODALS STATE --
    const [isDeleteTabConfirmOpen, setIsDeleteTabConfirmOpen] = useState(false);
    const [isDeleteItemConfirmOpen, setIsDeleteItemConfirmOpen] = useState(false);
    const [itemToDeleteIndex, setItemToDeleteIndex] = useState<number>(-1);

    const [editingTabName, setEditingTabName] = useState<string>(''); // For renaming
    const [editingTabOriginalName, setEditingTabOriginalName] = useState<string>(''); // To track renaming
    const [editingTabColor, setEditingTabColor] = useState<string>('#3b82f6');
    const [enableTabColor, setEnableTabColor] = useState<boolean>(true); // Handle optional color
    const [editingTabCols, setEditingTabCols] = useState<number>(defaultColumns);

    // -- DRAG AND DROP STATE --
    const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
    const [dragOverItemIndex, setDragOverItemIndex] = useState<number | null>(null);

    // -- TOUCH DRAG AND DROP STATE --
    const [touchStartY, setTouchStartY] = useState<number | null>(null);
    const [touchStartX, setTouchStartX] = useState<number | null>(null);
    const [touchCurrentY, setTouchCurrentY] = useState<number | null>(null);
    const [touchCurrentX, setTouchCurrentX] = useState<number | null>(null);
    const [touchDraggedIndex, setTouchDraggedIndex] = useState<number | null>(null);
    const [isTouchDragging, setIsTouchDragging] = useState<boolean>(false);
    const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
    const touchStartTimeRef = React.useRef<number | null>(null);
    const wasTouchDragRef = React.useRef<boolean>(false);

    // Threshold to distinguish between tap and drag (in pixels)
    const DRAG_THRESHOLD = 10;

    // -- HELPERS --
    const tabColors = useMemo(() => {
        return tabs.reduce(
            (acc, tab) => {
                if (data[tab].options?.color) {
                    acc[tab] = data[tab].options!.color!;
                }
                return acc;
            },
            {} as Record<string, string>,
        );
    }, [data, tabs]);

    const currentCategory = data[activeTab];
    const currentItems = currentCategory?.items || [];
    const specificColumns = currentCategory?.options?.cols;
    const effectiveColumns = specificColumns || defaultColumns;

    // -- TAB OPERATIONS --

    const handleAddTab = () => {
        setEditingTabOriginalName(''); // New Tab
        setEditingTabName('');
        setEnableTabColor(false); // Default to no custom color
        setEditingTabColor('#3b82f6');
        setEditingTabCols(4);
        setIsTabModalOpen(true);
    };

    const handleEditCurrentTab = () => {
        if (!currentCategory) return;
        setEditingTabOriginalName(activeTab);
        setEditingTabName(activeTab);

        // Check if color exists in options
        const existingColor = currentCategory.options?.color;
        setEnableTabColor(!!existingColor);
        setEditingTabColor(existingColor || '#3b82f6');

        setEditingTabCols(currentCategory.options?.cols || defaultColumns);
        setIsTabModalOpen(true);
    };

    const saveTab = () => {
        if (!editingTabName.trim()) return;

        // Validation: Check if name exists (only if renaming or creating new)
        if (editingTabOriginalName !== editingTabName && data[editingTabName]) {
            alert('A tab with this name already exists.');
            return;
        }

        setData((prev) => {
            // Construct the new content for the tab
            let items: BookmarkItem[] = [];
            if (editingTabOriginalName && prev[editingTabOriginalName]) {
                items = [...prev[editingTabOriginalName].items];
            }

            const newTabContent = {
                options: {
                    cols: editingTabCols,
                    // Only include color if enabled
                    ...(enableTabColor ? { color: editingTabColor } : {}),
                },
                items: items,
            };

            // If creating a completely new tab
            if (!editingTabOriginalName) {
                return {
                    ...prev,
                    [editingTabName]: newTabContent,
                };
            }

            // If editing/renaming, preserve key order by reconstructing object
            const newData: SpeedDialData = {};
            Object.keys(prev).forEach((key) => {
                if (key === editingTabOriginalName) {
                    // Replace old key with new key (which might be the same if not renaming)
                    newData[editingTabName] = newTabContent;
                } else {
                    newData[key] = prev[key];
                }
            });

            return newData;
        });

        // Update active tab if name changed or if it was new
        if (editingTabOriginalName !== editingTabName) {
            onTabChangeHandler(editingTabName);
        }

        setIsTabModalOpen(false);
    };

    const onTabChangeHandler = (tab: string) => {
        setActiveTab(tab);
        if (onTabChange) onTabChange(tab);
    };

    // Triggered by the button in the Edit Modal
    const requestDeleteTab = () => {
        setIsDeleteTabConfirmOpen(true);
    };

    // The actual deletion logic executed by the Confirmation Modal
    const executeDeleteTab = () => {
        if (!editingTabOriginalName) return;

        const remainingTabs = Object.keys(data).filter((t) => t !== editingTabOriginalName);
        const nextTab = remainingTabs.length > 0 ? remainingTabs[0] : '';

        setData((prev) => {
            const newData = { ...prev };
            delete newData[editingTabOriginalName];
            return newData;
        });

        onTabChangeHandler(nextTab);
        setIsDeleteTabConfirmOpen(false);
        setIsTabModalOpen(false);
    };

    // -- ITEM OPERATIONS --

    const handleAddItem = () => {
        setEditingItem({ title: '', url: '', thumbnail: '', description: '' });
        setEditingItemIndex(-1);
        setIsItemModalOpen(true);
    };

    const handleEditItem = (item: BookmarkItem, index: number) => {
        setEditingItem({ ...item });
        setEditingItemIndex(index);
        setIsItemModalOpen(true);
    };

    const handleDeleteItem = (index: number) => {
        setItemToDeleteIndex(index);
        setIsDeleteItemConfirmOpen(true);
    };

    // Triggered by the button in the Edit Item Modal
    const requestDeleteItem = () => {
        if (editingItemIndex >= 0) {
            setItemToDeleteIndex(editingItemIndex);
            setIsDeleteItemConfirmOpen(true);
        }
    };

    const executeDeleteItem = () => {
        if (itemToDeleteIndex === -1) return;

        setData((prev) => {
            const newData = { ...prev };
            const currentTab = { ...newData[activeTab] };
            currentTab.items = currentTab.items.filter((_, i) => i !== itemToDeleteIndex);

            newData[activeTab] = currentTab;
            return newData;
        });

        setIsDeleteItemConfirmOpen(false);
        setIsItemModalOpen(false);
        setItemToDeleteIndex(-1);
    };

    const saveItem = () => {
        if (!editingItem || !activeTab) return;

        setData((prev) => {
            const newData = { ...prev };
            const currentTab = { ...newData[activeTab] };
            // Deep copy items array
            const newItems = [...currentTab.items];

            if (editingItemIndex >= 0) {
                newItems[editingItemIndex] = editingItem;
            } else {
                newItems.push(editingItem);
            }

            currentTab.items = newItems;
            newData[activeTab] = currentTab;

            return newData;
        });

        setIsItemModalOpen(false);
    };

    // -- DRAG AND DROP HANDLERS --

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedItemIndex(index);
        // Set effect allowed
        e.dataTransfer.effectAllowed = 'move';
        // Optional: make the drag image transparent or set a custom one if needed
        // e.dataTransfer.setDragImage(e.currentTarget as Element, 0, 0);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault(); // Necessary to allow dropping
        if (draggedItemIndex === index) return;
        setDragOverItemIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverItemIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        setDragOverItemIndex(null);

        if (draggedItemIndex === null || draggedItemIndex === dropIndex) return;

        setData((prev) => {
            const newData = { ...prev };
            const currentTab = { ...newData[activeTab] };
            const newItems = [...currentTab.items];

            // Remove item from old position
            const [movedItem] = newItems.splice(draggedItemIndex, 1);
            // Insert at new position
            newItems.splice(dropIndex, 0, movedItem);

            currentTab.items = newItems;
            newData[activeTab] = currentTab;
            return newData;
        });

        setDraggedItemIndex(null);
    };

    // -- TOUCH DRAG AND DROP HANDLERS --

    const getElementIndexFromTouch = (clientY: number, clientX: number): number | null => {
        // Temporarily hide the dragged element to get accurate elementFromPoint
        const draggedElement = touchDraggedIndex !== null ? itemRefs.current[touchDraggedIndex] : null;
        const originalDisplay = draggedElement?.style.display;
        if (draggedElement) {
            draggedElement.style.display = 'none';
        }

        const element = document.elementFromPoint(clientX, clientY);

        // Restore display
        if (draggedElement) {
            draggedElement.style.display = originalDisplay || '';
        }

        if (!element) return null;

        // Find the parent container with the item index by checking refs
        for (let i = 0; i < itemRefs.current.length; i++) {
            const ref = itemRefs.current[i];
            if (ref && (ref === element || ref.contains(element))) {
                return i;
            }
        }

        return null;
    };

    const handleTouchStart = (e: React.TouchEvent, index: number) => {
        const touch = e.touches[0];
        setTouchStartY(touch.clientY);
        setTouchStartX(touch.clientX);
        setTouchCurrentY(touch.clientY);
        setTouchCurrentX(touch.clientX);
        setTouchDraggedIndex(index);
        setIsTouchDragging(false);
        wasTouchDragRef.current = false;
        touchStartTimeRef.current = Date.now();
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (touchDraggedIndex === null || touchStartY === null || touchStartX === null) return;

        const touch = e.touches[0];
        const deltaY = Math.abs(touch.clientY - touchStartY);
        const deltaX = Math.abs(touch.clientX - touchStartX);
        const totalDelta = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // Only start dragging if movement exceeds threshold
        if (totalDelta > DRAG_THRESHOLD) {
            e.preventDefault(); // Prevent scrolling while dragging
            setIsTouchDragging(true);
            setDraggedItemIndex(touchDraggedIndex);
            wasTouchDragRef.current = true;
        }

        setTouchCurrentY(touch.clientY);
        setTouchCurrentX(touch.clientX);

        // Find which element we're over (only if we're actually dragging)
        if (isTouchDragging || totalDelta > DRAG_THRESHOLD) {
            const overIndex = getElementIndexFromTouch(touch.clientY, touch.clientX);
            if (overIndex !== null && overIndex !== touchDraggedIndex) {
                setDragOverItemIndex(overIndex);
            } else {
                setDragOverItemIndex(null);
            }
        }
    };

    const handleTouchEnd = () => {
        if (touchDraggedIndex === null) {
            resetTouchState();
            return;
        }

        // Only perform reorder if we were actually dragging (not just a tap)
        if (isTouchDragging && touchCurrentY !== null && touchCurrentX !== null) {
            // Find drop target
            const dropIndex = getElementIndexFromTouch(touchCurrentY, touchCurrentX);

            if (dropIndex !== null && dropIndex !== touchDraggedIndex) {
                // Perform the reorder
                setData((prev) => {
                    const newData = { ...prev };
                    const currentTab = { ...newData[activeTab] };
                    const newItems = [...currentTab.items];

                    // Remove item from old position
                    const [movedItem] = newItems.splice(touchDraggedIndex, 1);
                    // Insert at new position
                    newItems.splice(dropIndex, 0, movedItem);

                    currentTab.items = newItems;
                    newData[activeTab] = currentTab;
                    return newData;
                });
            }
        }

        resetTouchState();
    };

    const resetTouchState = () => {
        // Use a small delay to prevent click events after touch end
        setTimeout(() => {
            wasTouchDragRef.current = false;
        }, 300);

        setTouchStartY(null);
        setTouchStartX(null);
        setTouchCurrentY(null);
        setTouchCurrentX(null);
        setTouchDraggedIndex(null);
        setIsTouchDragging(false);
        setDraggedItemIndex(null);
        setDragOverItemIndex(null);
        touchStartTimeRef.current = null;
    };

    // -- FINAL SAVE --
    const handleGlobalSave = () => {
        if (onSave) onSave(data);
    };

    // Safety check
    useEffect(() => {
        if (!tabs.includes(activeTab) && tabs.length > 0) {
            onTabChangeHandler(tabs[0]);
        }
    }, [data, tabs, activeTab]);

    return (
        <div className="w-full">
            {/* Editor Controls Header */}
            <div className="hidden mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800">
                <div>
                    <h2 className="text-lg font-bold text-blue-800 dark:text-blue-300">Editor Mode</h2>
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                        Drag items to reorder. Modify tabs and configuration.
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <TabNavigation
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={onTabChangeHandler}
                colors={tabColors}
                onAddTab={handleAddTab}
                onEditTab={activeTab ? handleEditCurrentTab : undefined}
                onSaveJson={handleGlobalSave}
                onEnableEdit={onEnableEdit}
            />

            {/* Grid CSS */}
            <style>{`
        .speed-dial-grid {
          display: grid;
          gap: 1rem;
          grid-template-columns: repeat(${Math.min(effectiveColumns, 2)}, minmax(0, 1fr));
        }
        @media (min-width: 640px) {
          .speed-dial-grid {
            gap: 1.5rem;
            grid-template-columns: repeat(${Math.min(effectiveColumns, 3)}, minmax(0, 1fr));
          }
        }
        @media (min-width: 1024px) {
          .speed-dial-grid {
            grid-template-columns: repeat(${Math.min(effectiveColumns, 4)}, minmax(0, 1fr));
          }
        }
        @media (min-width: 1280px) {
          .speed-dial-grid {
            grid-template-columns: repeat(${effectiveColumns}, minmax(0, 1fr));
          }
        }
      `}</style>

            {/* Content Grid */}
            <div className="speed-dial-grid pb-20">
                {/* Existing Items */}
                {currentItems.map((item, index) => {
                    const isDragging = draggedItemIndex === index || (touchDraggedIndex === index && isTouchDragging);
                    const isDragOver = dragOverItemIndex === index;
                    const touchOffsetY =
                        touchDraggedIndex === index && isTouchDragging && touchStartY !== null && touchCurrentY !== null
                            ? touchCurrentY - touchStartY
                            : 0;
                    const touchOffsetX =
                        touchDraggedIndex === index && isTouchDragging && touchStartX !== null && touchCurrentX !== null
                            ? touchCurrentX - touchStartX
                            : 0;

                    return (
                        <div
                            key={`${item.url}-${index}`}
                            ref={(el) => {
                                itemRefs.current[index] = el;
                            }}
                            className={`
                  w-full min-w-0 h-full transition-all duration-200
                  ${isDragging ? 'opacity-40 scale-95 grayscale z-50' : 'opacity-100'}
                  ${isDragOver ? 'scale-105' : ''}
              `}
                            style={{
                                transform:
                                    touchDraggedIndex === index && isTouchDragging
                                        ? `translate(${touchOffsetX}px, ${touchOffsetY}px)`
                                        : undefined,
                                position: touchDraggedIndex === index && isTouchDragging ? 'relative' : undefined,
                                touchAction: 'none', // Prevent default touch behaviors
                            }}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onTouchStart={(e) => handleTouchStart(e, index)}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                        >
                            {/* Wrap card in a container that handles the cursor style */}
                            <div
                                className="h-full cursor-move"
                                onClick={(e) => {
                                    // Prevent edit if it was a drag operation
                                    if (wasTouchDragRef.current) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        return;
                                    }
                                }}
                            >
                                <SpeedDialCard
                                    item={item}
                                    onEdit={() => {
                                        // Prevent edit if it was a drag operation
                                        if (wasTouchDragRef.current || isTouchDragging) {
                                            return;
                                        }
                                        handleEditItem(item, index);
                                    }}
                                />
                            </div>
                        </div>
                    );
                })}

                {/* Add New Item Card - Not Draggable */}
                {activeTab && (
                    <div className="w-full min-w-0 h-full min-h-[200px]">
                        <SpeedDialCard isAddNew onClick={handleAddItem} />
                    </div>
                )}
            </div>

            {/* --- MODALS --- */}

            {/* 1. ITEM MODAL */}
            <Modal
                isOpen={isItemModalOpen}
                onClose={() => setIsItemModalOpen(false)}
                title={editingItemIndex >= 0 ? 'Edit Item' : 'Add New Item'}
                onSave={saveItem}
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                        <input
                            type="text"
                            title="Title"
                            className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-gray-300 dark:border-slate-700 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-white"
                            value={editingItem?.title || ''}
                            onChange={(e) =>
                                setEditingItem((prev) => (prev ? { ...prev, title: e.target.value } : null))
                            }
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL</label>
                        <input
                            type="url"
                            title="URL"
                            className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-gray-300 dark:border-slate-700 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-white"
                            value={editingItem?.url || ''}
                            onChange={(e) => setEditingItem((prev) => (prev ? { ...prev, url: e.target.value } : null))}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Thumbnail URL
                        </label>
                        <input
                            type="url"
                            title="Thumbnail URL"
                            className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-gray-300 dark:border-slate-700 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-white"
                            value={editingItem?.thumbnail || ''}
                            onChange={(e) =>
                                setEditingItem((prev) => (prev ? { ...prev, thumbnail: e.target.value } : null))
                            }
                        />
                        {editingItem?.thumbnail && (
                            <div className="mt-2 h-20 w-32 rounded overflow-hidden border border-gray-200">
                                <img src={editingItem.thumbnail} alt="Preview" className="w-full h-full object-cover" />
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Description
                        </label>
                        <textarea
                            rows={3}
                            title="Description"
                            className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-gray-300 dark:border-slate-700 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-white"
                            value={editingItem?.description || ''}
                            onChange={(e) =>
                                setEditingItem((prev) => (prev ? { ...prev, description: e.target.value } : null))
                            }
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Open in
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1 font-normal">
                                (optional)
                            </span>
                        </label>
                        <select
                            title="Target"
                            className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-gray-300 dark:border-slate-700 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-white"
                            value={editingItem?.target || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                setEditingItem((prev) => {
                                    if (!prev) return null;
                                    // If empty string, remove the target property
                                    if (value === '') {
                                        const { target, ...rest } = prev;
                                        return rest as BookmarkItem;
                                    }
                                    return { ...prev, target: value as 'blank' | 'self' };
                                });
                            }}
                        >
                            <option value="self">Default (Same window)</option>
                            <option value="blank">New tab</option>
                        </select>
                    </div>

                    {editingItemIndex >= 0 && (
                        <div className="mt-6 pt-4 border-t border-gray-100 dark:border-slate-800 flex justify-end">
                            <button
                                onClick={requestDeleteItem}
                                className="flex px-4 py-2 text-sm font-medium text-white rounded-lg shadow-md transition-all bg-red-600 hover:bg-red-700 active:bg-red-800 shadow-red-500/20"
                            >
                                <svg className="w-4 h-4 me-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                                Delete Item
                            </button>
                        </div>
                    )}
                </div>
            </Modal>

            {/* 2. TAB MODAL */}
            <Modal
                isOpen={isTabModalOpen}
                onClose={() => setIsTabModalOpen(false)}
                title={editingTabOriginalName ? 'Edit Tab Options' : 'New Tab'}
                onSave={saveTab}
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Tab Name
                        </label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-gray-300 dark:border-slate-700 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-white"
                            value={editingTabName}
                            onChange={(e) => setEditingTabName(e.target.value)}
                            placeholder="e.g., Work, Social..."
                        />
                    </div>

                    <div className="space-y-6">
                        {/* Columns Slider */}
                        <div>
                            <div className="flex justify-between mb-1">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Columns
                                </label>
                                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                                    {editingTabCols}
                                </span>
                            </div>
                            <input
                                type="range"
                                title="Columns"
                                min="1"
                                max="8"
                                step="1"
                                className="w-full h-2 bg-gray-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer accent-blue-600 hover:accent-blue-700 focus:outline-none"
                                value={editingTabCols}
                                onChange={(e) => setEditingTabCols(Number(e.target.value))}
                            />
                        </div>

                        {/* Color Picker */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Tab Color
                                </label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="useColor"
                                        checked={enableTabColor}
                                        onChange={(e) => setEnableTabColor(e.target.checked)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                    />
                                    <label
                                        htmlFor="useColor"
                                        className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none"
                                    >
                                        Custom
                                    </label>
                                </div>
                            </div>

                            {enableTabColor ? (
                                <div className="flex gap-2 items-center bg-gray-50 dark:bg-slate-800 p-2 rounded-lg border border-gray-200 dark:border-slate-700">
                                    <input
                                        type="color"
                                        title="Tab Color"
                                        className="h-8 w-10 cursor-pointer bg-transparent border-none p-0"
                                        value={editingTabColor}
                                        onChange={(e) => setEditingTabColor(e.target.value)}
                                    />
                                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400 uppercase">
                                        {editingTabColor}
                                    </span>
                                </div>
                            ) : (
                                <div className="p-2 text-xs text-gray-400 dark:text-gray-500 italic bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 text-center py-3">
                                    Default Theme Color
                                </div>
                            )}
                        </div>
                    </div>

                    {editingTabOriginalName && (
                        <div className="mt-6 pt-4 border-t border-gray-100 dark:border-slate-800 flex justify-end">
                            <button
                                onClick={requestDeleteTab}
                                className="flex px-4 py-2 text-sm font-medium text-white rounded-lg shadow-md transition-all bg-red-600 hover:bg-red-700 active:bg-red-800 shadow-red-500/20"
                            >
                                <svg className="w-4 h-4 me-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                                Delete Tab
                            </button>
                        </div>
                    )}
                </div>
            </Modal>

            {/* 3. DELETE TAB CONFIRMATION MODAL */}
            <Modal
                isOpen={isDeleteTabConfirmOpen}
                title="Delete Tab"
                onClose={() => setIsDeleteTabConfirmOpen(false)}
                onSave={executeDeleteTab}
                saveLabel="Delete"
                variant="danger"
            >
                <div className="text-gray-600 dark:text-slate-300">
                    <p>
                        Are you sure you want to delete the tab{' '}
                        <span className="font-bold text-slate-800 dark:text-white">"{editingTabOriginalName}"</span>?
                    </p>
                    <p className="mt-2 text-sm text-red-500 dark:text-red-400">
                        This action cannot be undone and all items in this tab will be lost.
                    </p>
                </div>
            </Modal>

            {/* 4. DELETE ITEM CONFIRMATION MODAL */}
            <Modal
                isOpen={isDeleteItemConfirmOpen}
                title="Delete Item"
                onClose={() => setIsDeleteItemConfirmOpen(false)}
                onSave={executeDeleteItem}
                saveLabel="Delete"
                variant="danger"
            >
                <div className="text-gray-600 dark:text-slate-300">
                    <p>Are you sure you want to delete this item?</p>
                    {itemToDeleteIndex !== -1 && currentItems[itemToDeleteIndex] && (
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-slate-800 rounded border border-gray-200 dark:border-slate-700 flex items-center gap-3">
                            {currentItems[itemToDeleteIndex].thumbnail ? (
                                <img
                                    src={currentItems[itemToDeleteIndex].thumbnail}
                                    alt=""
                                    className="w-10 h-10 object-cover rounded"
                                />
                            ) : (
                                <div className="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded flex items-center justify-center">
                                    <span className="text-xs">No Img</span>
                                </div>
                            )}
                            <span className="font-medium text-slate-800 dark:text-white truncate">
                                {currentItems[itemToDeleteIndex].title}
                            </span>
                        </div>
                    )}
                    <p className="mt-4 text-sm text-red-500 dark:text-red-400">This action cannot be undone.</p>
                </div>
            </Modal>
        </div>
    );
};

export default SpeedDialEdit;
